
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Appointment } from '../types/appointmentTypes';
import { useBookingRealtimeSync } from '@/hooks/useBookingRealtimeSync';

export const useAppointments = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [refreshTrigger, setRefreshTrigger] = useState<number>(0);

  const fetchAppointments = useCallback(async () => {
    if (!user) {
      console.log('No user found, skipping fetch');
      setIsLoading(false);
      return;
    }
    
    try {
      setIsLoading(true);
      console.log('Fetching upcoming appointments for user:', user.id);
      
      // Get the current date and time
      const now = new Date().toISOString();
      
      const { data, error } = await supabase
        .from('bookings')
        .select(`
          id,
          start_time,
          end_time,
          status,
          format,
          price_at_booking,
          currency_at_booking,
          client_notes,
          location_details,
          session_instructions,
          refund_status,
          refund_reason,
          payment_status,
          service:services!inner (service_name),
          practitioner:practitioner_id (first_name, last_name, avatar_url),
          practitioner_id
        `)
        .eq('client_id', user.id)
        .eq('status', 'Scheduled')  // Only show confirmed scheduled appointments, not Pending Payment
        .gte('start_time', now)     // Only future appointments
        .order('start_time', { ascending: true });
        
      if (error) throw error;
      
      if (data) {
        console.log(`Fetched ${data.length} upcoming appointments from database:`, data);
        const formattedData: Appointment[] = data.map(item => ({
          id: item.id,
          start_time: item.start_time,
          end_time: item.end_time,
          status: item.status,
          format: item.format,
          price_at_booking: item.price_at_booking,
          currency_at_booking: item.currency_at_booking,
          client_notes: item.client_notes,
          location_details: item.location_details,
          session_instructions: (item as any).session_instructions,
          refund_status: item.refund_status,
          refund_reason: item.refund_reason,
          payment_status: item.payment_status || 'Unknown',
          service: {
            service_name: item.service?.service_name || 'Unknown Service'
          },
          practitioner: {
            first_name: item.practitioner?.first_name || null,
            last_name: item.practitioner?.last_name || null,
            avatar_url: item.practitioner?.avatar_url || null
          },
          practitioner_id: item.practitioner_id
        }));
        
        setAppointments(formattedData);
      } else {
        setAppointments([]);
      }
    } catch (error) {
      console.error('Error fetching appointments:', error);
      setAppointments([]); // Set empty array on error instead of leaving undefined
      toast({
        title: "Failed to load appointments",
        description: error instanceof Error ? error.message : "There was an error loading your appointments",
        variant: "destructive",
        duration: 8000,
      });
    } finally {
      setIsLoading(false);
    }
  }, [user, toast]);

  // Refresh data when the component mounts, when user changes, or when refreshTrigger changes
  useEffect(() => {
    fetchAppointments();
  }, [fetchAppointments, refreshTrigger]);

  // Function to manually trigger a refresh
  const refreshAppointments = useCallback(() => {
    setRefreshTrigger(prev => prev + 1);
  }, []);

  // Realtime: Monitor booking updates
  useBookingRealtimeSync({
    userId: user?.id,
    type: 'client',
    onBookingUpdate: (updatedBooking) => {
      console.log('Booking update received in upcoming:', updatedBooking);
      setAppointments(appointments => {
        // Check if the appointment already exists in the state
        const existingAppointmentIndex = appointments.findIndex(appt => appt.id === updatedBooking.id);
        
        if (existingAppointmentIndex >= 0) {
          // Update existing appointment
          const updatedAppointments = [...appointments];
          updatedAppointments[existingAppointmentIndex] = {
            ...updatedAppointments[existingAppointmentIndex],
            ...updatedBooking,
            // Ensure payment_status is properly updated
            payment_status: updatedBooking.payment_status || updatedAppointments[existingAppointmentIndex].payment_status
          };
          return updatedAppointments;
        } else {
          // This should be handled by onBookingInsert, but just in case
          return appointments;
        }
      });
    },
    onBookingInsert: (newBooking) => {
      console.log('New booking received in upcoming:', newBooking);
      // Check if the new booking should be shown in the upcoming section
      const now = new Date();
      
      if (newBooking.status === 'Scheduled' && new Date(newBooking.start_time) > now) {
        fetchAppointments(); // Re-fetch all appointments to ensure we have complete data
      }
    }
  });

  return { 
    appointments, 
    isLoading, 
    setAppointments,
    fetchAppointments,
    refreshAppointments 
  };
};
